/**
 * 中国大陆出口 IP 判定（对齐 App 侧 ipconfig.dart 探测源与策略）。
 * 仅看当前出口网络；全部失败按非大陆（fail-open）。
 *
 * 开关 VPN 后需重新探测：充值/提现门禁会 forceRefresh；
 * 页面重新可见 / online 时清缓存。
 */

const STORAGE_KEY = 'deepsage:china-mainland:v2';
const PROBE_TIMEOUT_MS = 8000;
/** 非强制刷新时最多复用多久（毫秒） */
const CACHE_TTL_MS = 15_000;

/** @type {{ value: boolean, at: number } | null} */
let cached = null;
/** @type {Promise<boolean> | null} */
let inFlight = null;
let lifecycleAttached = false;

/**
 * @param {string | null | undefined} code
 * @returns {boolean | null} true/false 已判定；null 无法从该字段判断
 */
export function countryCodeIsChina(code) {
  const raw = String(code ?? '').trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === 'CN' || upper === 'CHINA') return true;
  if (raw === '中国' || raw === '中國') return true;
  if (/^[A-Z]{2}$/.test(upper)) return false;
  return null;
}

/**
 * @param {string} text
 * @returns {boolean | null}
 */
export function parseCloudflareTraceLoc(text) {
  const lines = String(text ?? '').split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line.startsWith('loc=')) continue;
    return countryCodeIsChina(line.slice(4));
  }
  return null;
}

/**
 * @param {unknown} root
 * @returns {boolean | null}
 */
export function parseBaiduGeoPayload(root) {
  if (!root || typeof root !== 'object') return null;
  const data = /** @type {{ data?: unknown }} */ (root).data;
  if (!data || typeof data !== 'object') return null;
  const map = /** @type {Record<string, unknown>} */ (data);
  return countryCodeIsChina(map.country_code ?? map.country ?? '');
}

/**
 * @param {string} text
 * @returns {boolean | null}
 */
export function parsePconlinePayload(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;
  let map;
  try {
    map = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!map || typeof map !== 'object') return null;
  const err = String(map.err ?? '').trim();
  if (err) return null;
  const proCode = String(map.proCode ?? '').trim();
  if (proCode && proCode !== '999999') return true;
  return null;
}

/**
 * country.is 现多为 JSON：{"ip":"...","country":"CN"}；兼容纯文本 CN。
 * @param {string} text
 * @returns {boolean | null}
 */
export function parseCountryIsPayload(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;
  const direct = countryCodeIsChina(trimmed);
  if (direct != null) return direct;
  try {
    const data = JSON.parse(trimmed);
    return countryCodeIsChina(data?.country ?? data?.country_code ?? '');
  } catch {
    return null;
  }
}

function readPersisted() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = String(sessionStorage.getItem(STORAGE_KEY) ?? '').trim();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = parsed?.value;
    const at = Number(parsed?.at);
    if (typeof value !== 'boolean' || !Number.isFinite(at)) {
      clearPersisted();
      return null;
    }
    if (Date.now() - at > CACHE_TTL_MS) {
      clearPersisted();
      return null;
    }
    return { value, at };
  } catch {
    clearPersisted();
    return null;
  }
}

function writePersisted(entry) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // ignore
  }
}

function clearPersisted() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    // 清掉旧版布尔缓存
    sessionStorage.removeItem('deepsage:china-mainland:v1');
  } catch {
    // ignore
  }
}

function isFresh(entry) {
  return Boolean(entry) && Date.now() - entry.at <= CACHE_TTL_MS;
}

/**
 * @param {string} name
 * @param {(signal: AbortSignal) => Promise<boolean | null>} probe
 * @returns {Promise<boolean>}
 */
async function runProbe(name, probe) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const result = await probe(controller.signal);
    if (result == null) throw new Error('miss');
    if (process.env.NODE_ENV === 'development') {
      console.debug('[china-mainland] probe hit', { name, mainland: Boolean(result) });
    }
    return Boolean(result);
  } finally {
    clearTimeout(timer);
    controller.abort();
  }
}

async function probeCloudflareTrace(url, signal) {
  const res = await fetch(url, {
    signal,
    headers: { Accept: 'text/plain' },
  });
  if (!res.ok) return null;
  return parseCloudflareTraceLoc(await res.text());
}

/** @type {Array<[string, (signal: AbortSignal) => Promise<boolean | null>]>} */
const GEO_IP_PROBES = [
  ['cloudflare.com', (signal) => probeCloudflareTrace('https://cloudflare.com/cdn-cgi/trace', signal)],
  ['1.1.1.1', (signal) => probeCloudflareTrace('https://1.1.1.1/cdn-cgi/trace', signal)],
  [
    'ipwho.is',
    async (signal) => {
      const res = await fetch('https://ipwho.is/', { signal });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.success === false) return null;
      return countryCodeIsChina(data?.country_code);
    },
  ],
  [
    'ipapi.co',
    async (signal) => {
      const res = await fetch('https://ipapi.co/json/', { signal });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.error) return null;
      return countryCodeIsChina(data?.country_code ?? data?.country);
    },
  ],
  [
    'geojs.io',
    async (signal) => {
      const res = await fetch('https://get.geojs.io/v1/ip/country.json', { signal });
      if (!res.ok) return null;
      const data = await res.json();
      return countryCodeIsChina(data?.country ?? data?.name);
    },
  ],
  [
    'country.is',
    async (signal) => {
      const res = await fetch('https://api.country.is/', {
        signal,
        headers: { Accept: 'application/json, text/plain, */*' },
      });
      if (!res.ok) return null;
      return parseCountryIsPayload(await res.text());
    },
  ],
  [
    'baidu-geo',
    async (signal) => {
      const res = await fetch('https://qifu-api.baidubce.com/ip/geo/v1/district', { signal });
      if (!res.ok) return null;
      return parseBaiduGeoPayload(await res.json());
    },
  ],
  [
    'pconline',
    async (signal) => {
      const res = await fetch('https://whois.pconline.com.cn/ipJson.jsp?json=true', {
        signal,
        headers: { Accept: 'application/json, text/plain, */*' },
      });
      if (!res.ok) return null;
      return parsePconlinePayload(await res.text());
    },
  ],
];

async function probeCurrentIsMainland() {
  try {
    return await Promise.any(GEO_IP_PROBES.map(([name, probe]) => runProbe(name, probe)));
  } catch {
    // 全部并行失败，顺序重试
  }

  for (const [name, probe] of GEO_IP_PROBES) {
    try {
      return await runProbe(name, probe);
    } catch {
      // try next
    }
  }

  if (process.env.NODE_ENV === 'development') {
    console.warn('[china-mainland] all probes failed; treat as non-mainland');
  }
  return false;
}

function commit(value) {
  const entry = { value, at: Date.now() };
  cached = entry;
  writePersisted(entry);
  if (process.env.NODE_ENV === 'development') {
    console.debug('[china-mainland]', { mainland: value });
  }
}

function attachLifecycleRefresh() {
  if (lifecycleAttached || typeof window === 'undefined') return;
  lifecycleAttached = true;

  const invalidate = () => {
    cached = null;
    inFlight = null;
    clearPersisted();
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') invalidate();
  });
  window.addEventListener('online', invalidate);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) invalidate();
  });
}

/**
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export function resolveIsChinaMainlandUser({ forceRefresh = false } = {}) {
  attachLifecycleRefresh();

  if (forceRefresh) {
    cached = null;
    clearPersisted();
    inFlight = null;
  }

  if (!forceRefresh && isFresh(cached)) return Promise.resolve(cached.value);

  if (!forceRefresh) {
    const persisted = readPersisted();
    if (persisted) {
      cached = persisted;
      return Promise.resolve(persisted.value);
    }
  }

  if (!forceRefresh && inFlight) return inFlight;

  inFlight = probeCurrentIsMainland()
    .then((value) => {
      commit(value);
      return value;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function resetChinaMainlandRegion() {
  cached = null;
  inFlight = null;
  clearPersisted();
}

export function getChinaMainlandCached() {
  return cached?.value ?? null;
}
